import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';

const SearchableClarification = ({ clarificationText, clarificationFields, onSubmit, isLoading }) => {
  // State to track selected values for each field
  const [selections, setSelections] = useState({});
  const [openPopovers, setOpenPopovers] = useState({});

  // Handle field selection
  const handleFieldChange = (fieldType, value) => {
    setSelections(prev => ({
      ...prev,
      [fieldType]: value
    }));
  };

  // Handle submission
  const handleSubmit = () => {
    // Validate all fields are filled
    const allFieldsFilled = clarificationFields.every(field => {
      return selections[field.type] && selections[field.type].trim() !== '';
    });

    if (!allFieldsFilled) {
      return; // Don't submit if not all fields are filled
    }

    // Construct natural language clarification message
    const clarificationMessage = clarificationFields.map(field => {
      return `${field.name}: ${selections[field.type]}`;
    }).join('\n');

    onSubmit(clarificationMessage);
  };

  return (
    <Card className="border-none shadow-lg">
      <CardContent className="p-6 space-y-6">
        {/* Display clarification text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900 font-medium">{clarificationText}</p>
        </div>

        {/* Render fields dynamically */}
        {clarificationFields.map((field, index) => (
          <div key={index} className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">
              {field.name}
              {field.searchable && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Searchable
                </Badge>
              )}
            </Label>

            {field.searchable ? (
              // Searchable dropdown using Command component
              <Popover 
                open={openPopovers[field.type]} 
                onOpenChange={(open) => setOpenPopovers(prev => ({ ...prev, [field.type]: open }))}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between text-left font-normal"
                  >
                    <span className={selections[field.type] ? "text-slate-900" : "text-slate-500"}>
                      {selections[field.type] || field.placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder={`Search ${field.name.toLowerCase()}...`} />
                    <CommandList>
                      <CommandEmpty>No options found.</CommandEmpty>
                      <CommandGroup>
                        {field.options.map((option, optionIndex) => (
                          <CommandItem
                            key={optionIndex}
                            value={option}
                            onSelect={() => {
                              handleFieldChange(field.type, option);
                              setOpenPopovers(prev => ({ ...prev, [field.type]: false }));
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                selections[field.type] === option ? 'opacity-100' : 'opacity-0'
                              }`}
                            />
                            {option}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              // Regular dropdown for small option lists
              <select
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={selections[field.type] || ''}
                onChange={(e) => handleFieldChange(field.type, e.target.value)}
              >
                <option value="">{field.placeholder}</option>
                {field.options.map((option, optionIndex) => (
                  <option key={optionIndex} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}

        {/* Submit button */}
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !clarificationFields.every(field => selections[field.type])}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-6 rounded-lg transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>Processing...</span>
            </div>
          ) : (
            'Continue with Selection'
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SearchableClarification;

